import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { isAuthenticated } from '@/lib/auth';
import { useClerkPool } from '@/components/MyEventsAuthProvider';

/**
 * Gates the authenticated routes.
 *
 * Accepts either identity while My Events runs both: a Clerk session, or the
 * legacy cookie from password sign-in. Requiring only the legacy one would
 * bounce every Clerk-signed-in customer straight back to the login screen they
 * just completed.
 *
 * Clerk is still loading on the first render after a hard refresh, which is
 * neither signed in nor signed out. Redirecting then would throw people out
 * mid-restore, so that state waits.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { mounted: clerkMounted } = useClerkPool();

  // Hooks must not be conditional, and useAuth throws outside a provider —
  // which is exactly the case when no Clerk key is configured. The component
  // is split so the hook only ever runs where a provider exists.
  return clerkMounted ? (
    <ClerkAwareGuard location={location}>{children}</ClerkAwareGuard>
  ) : (
    <LegacyGuard location={location}>{children}</LegacyGuard>
  );
}

/** Guard for environments with Clerk mounted. */
function ClerkAwareGuard({
  children,
  location,
}: {
  children: React.ReactNode;
  location: ReturnType<typeof useLocation>;
}) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn && !isAuthenticated()) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/** Guard for environments without Clerk — the pre-migration behaviour. */
function LegacyGuard({
  children,
  location,
}: {
  children: React.ReactNode;
  location: ReturnType<typeof useLocation>;
}) {
  if (!isAuthenticated()) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
